/**
 * Authentication Context
 * Provides auth state to the entire application
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useSession } from '~/lib/auth-client';

interface AuthContextValue {
  user: {
    id: string;
    email: string;
    name: string | null;
    emailVerified: boolean;
  } | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Only run auth session check on client-side to avoid SSR issues
  const isServer = typeof window === 'undefined';
  const { data: session, isPending, refetch } = isServer
    ? { data: null, isPending: false, refetch: () => {} }
    : useSession();

  const value: AuthContextValue = {
    user: session?.user || null,
    isLoading: isPending,
    isAuthenticated: !!session?.user,
    refetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
